<template>
  <div class="data-export-wrap">
    <main class="page">

      <p class="breadcrumb">Swiss Eating Quiz / Internal / Data Exports</p>

      <h1 class="page-title">Data Exports</h1>
      <p class="page-subtitle">
        Download datasets for analysis. All files include a README with content
        description, generation date, anonymisation rules, and a citation line.
      </p>

      <div class="meta-strip">
        <div v-for="m in meta" :key="m.label" class="meta-item">
          <span class="meta-label">{{ m.label }}</span>
          <span class="meta-value">{{ m.value }}</span>
        </div>
      </div>

      <div class="privacy-note">
        <p class="privacy-note-title">A note on anonymisation</p>
        <p class="privacy-note-text">
          Datasets marked as "sensitive" contain per-submission rows. Cantons
          with fewer than 5 submissions in the period are bucketed as "rest of
          Switzerland" to prevent re-identification. Timestamps are rounded to
          the day. No email addresses or IP addresses are ever included in any
          export.
        </p>
      </div>

      <section
        v-for="section in sections"
        :key="section.title"
        class="section"
      >
        <h2 class="section-title">{{ section.title }}</h2>
        <p class="section-intro">{{ section.intro }}</p>

        <div v-if="section.note" class="privacy-note">
          <p class="privacy-note-title">{{ section.note.title }}</p>
          <p class="privacy-note-text">{{ section.note.body }}</p>
        </div>

        <div class="dataset-grid">
          <div
            v-for="item in section.items"
            :key="item.name"
            class="dataset-card"
          >
            <div class="dataset-info">
              <div class="dataset-header">
                <p class="dataset-name">{{ item.name }}</p>
                <span class="dataset-format" :class="item.format">{{ item.format.toUpperCase() }}</span>
                <span v-if="item.sensitive" class="dataset-tag sensitive">Sensitive</span>
              </div>
              <p class="dataset-description">{{ item.description }}</p>
              <div class="dataset-meta">
                <span v-if="item.rows" class="dataset-meta-item"><strong>{{ item.rows }}</strong></span>
                <span v-if="item.size" class="dataset-meta-item"><strong>{{ item.size }}</strong></span>
                <span v-if="item.columns" class="dataset-meta-item">{{ item.columns }}</span>
                <span v-if="item.version" class="dataset-meta-item"><strong>{{ item.version }}</strong></span>
                <span v-if="item.versioned" class="dataset-meta-item">Versioned</span>
                <span v-if="item.lastRevised" class="dataset-meta-item">Last revised <strong>{{ item.lastRevised }}</strong></span>
                <span v-if="item.updated" class="dataset-meta-item">Updated <strong>{{ item.updated }}</strong></span>
              </div>
            </div>
            <button
              type="button"
              class="download-button"
              @click="downloadDataset(item)"
            >Download</button>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Full bundle</h2>
        <p class="section-intro">
          Everything above in a single zip, ready for archival or for sending
          to a collaborator.
        </p>

        <div class="bulk-card">
          <div>
            <p class="bulk-card-title">Complete data package</p>
            <p class="bulk-card-desc">
              All datasets, the question dictionary, the scoring methodology,
              the codebook, and a top-level README. Generated on demand, takes
              about 30 seconds to assemble.
            </p>
          </div>
          <button
            type="button"
            class="download-button"
            @click="downloadBundle"
          >Generate and download (~4.5 MB)</button>
        </div>
      </section>

      <div class="footer">
        <span>Internal use only · BFH Departement Gesundheit</span>
        <span>Mock data · v0.1 · 9 May 2026</span>
      </div>

    </main>
  </div>
</template>

<script>
export default {
  name: 'DataExport',
  data() {
    return {
      meta: [
        { label: 'Period', value: 'Project to date' },
        { label: 'Format', value: 'UTF-8, comma-separated' },
        { label: 'Last regenerated', value: '9 May 2026, 03:00 (nightly)' },
        { label: 'Total submissions', value: '12 847' },
      ],
      sections: [
        {
          title: 'Aggregate datasets',
          intro: 'Pre-computed summary statistics. Safe to share, no privacy considerations. These are the same numbers shown on the participation dashboard.',
          items: [
            {
              name: 'Submissions per canton',
              format: 'csv',
              description: 'One row per canton with submission count, percentage of total, and language breakdown.',
              rows: '26 rows',
              size: '4 KB',
              updated: 'nightly',
            },
            {
              name: 'Submissions per day',
              format: 'csv',
              description: 'Daily submission counts since project launch. One row per day, with cumulative total and 7-day rolling average.',
              rows: '~220 rows',
              size: '12 KB',
              updated: 'nightly',
            },
            {
              name: 'Hour-of-day distribution',
              format: 'csv',
              description: 'Submission counts by hour, aggregated across the project. 24 rows.',
              rows: '24 rows',
              size: '2 KB',
              updated: 'nightly',
            },
            {
              name: 'International submissions',
              format: 'csv',
              description: 'Country-level counts for submissions originating outside Switzerland.',
              rows: '30 rows',
              size: '3 KB',
              updated: 'nightly',
            },
          ],
        },
        {
          title: 'Per-submission datasets',
          intro: 'One row per anonymous quiz submission. The core dataset for analysis and publication. Anonymisation rules apply (see note above).',
          items: [
            {
              name: 'Full quiz responses',
              format: 'csv',
              sensitive: true,
              description: 'All 67 answers per submission, plus the seven group scores and the overall score. Includes language, participant type (self / parent / teacher), age band, and date.',
              rows: '12 847 rows',
              size: '~3.2 MB',
              columns: '76 columns',
              updated: 'nightly',
            },
            {
              name: 'Scores only',
              format: 'csv',
              sensitive: true,
              description: 'Slimmer version of the above: just the seven group scores and overall score per submission, plus context columns. Useful when full answer data is not needed.',
              rows: '12 847 rows',
              size: '~890 KB',
              columns: '12 columns',
              updated: 'nightly',
            },
            {
              name: 'Drop-out analysis',
              format: 'csv',
              description: 'Submissions that started but were not completed. Records the question at which the participant left, time spent, language, and date. Useful for identifying difficult or unclear questions.',
              rows: '~2 100 rows',
              size: '~140 KB',
              updated: 'nightly',
            },
            {
              name: 'Reminder cohort',
              format: 'csv',
              description: 'Anonymised counts of participants who opted into the 3-month reminder, broken down by month and language. No email addresses included.',
              rows: '~30 rows',
              size: '3 KB',
              updated: 'nightly',
            },
          ],
        },
        {
          title: 'Data quality and integrity',
          intro: "SEQ actively monitors submissions for indicators of automated or duplicate input. The reports below let the research team review flagged submissions, inspect timing distributions, and document the data integrity decisions behind any published analysis. Submissions flagged here may be excluded from published datasets at the team's discretion.",
          note: {
            title: 'Methodology',
            body: 'All flagging is automated and based on documented heuristics: submission timing, honeypot field state, rate-limit events, and pattern-similarity scoring. The full methodology is described in the Data Integrity Methodology PDF below. The team reviews flagged submissions periodically and may decide to exclude them from analyses.',
          },
          items: [
            {
              name: 'Suspicious submissions report',
              format: 'csv',
              sensitive: true,
              description: 'Submissions flagged by automated heuristics. One row per flagged submission with the reason (too_fast / honeypot_triggered / rate_limit_hit / pattern_anomaly), the time elapsed from start to submit, language, and date. Reviewed by the team before any data freeze.',
              rows: '~80 rows',
              size: '~12 KB',
              columns: '8 columns',
              updated: 'nightly',
            },
            {
              name: 'Submission timing distribution',
              format: 'csv',
              description: 'Histogram of completion times across all submissions. Bucketed in 15-second intervals from 0 to 30 minutes. Useful for spotting timing anomalies and validating thresholds.',
              rows: '120 rows',
              size: '4 KB',
              updated: 'nightly',
            },
            {
              name: 'Rate-limit events',
              format: 'csv',
              description: 'Aggregated counts of requests blocked by the rate limiter. Daily breakdown by reason (per-IP-hour exceeded, per-IP-day exceeded). No IP addresses included; only counts and dates.',
              rows: '~220 rows',
              size: '~14 KB',
              updated: 'nightly',
            },
            {
              name: 'Honeypot triggers',
              format: 'csv',
              description: 'Counts of bot attempts caught by the honeypot field on the consent screen. Daily totals plus a running cumulative count. Indicator of automated traffic volume.',
              rows: '~220 rows',
              size: '8 KB',
              updated: 'nightly',
            },
            {
              name: 'Duplicate-pattern clusters',
              format: 'csv',
              sensitive: true,
              description: 'Submissions whose answer patterns and timing match each other within a tight similarity threshold. Clustered into groups; one row per cluster with size, time window, and language. Useful for identifying coordinated submissions or single-source flooding.',
              rows: '~25 rows',
              size: '~6 KB',
              updated: 'nightly',
            },
            {
              name: 'Admin authentication log',
              format: 'csv',
              description: 'Failed admin login attempts by day. Counts only; no usernames or IP addresses. Indicator of brute-force activity against the admin interface.',
              rows: '~30 rows',
              size: '3 KB',
              updated: 'nightly',
            },
            {
              name: 'Data Integrity Methodology',
              format: 'pdf',
              description: 'Full methodology behind the integrity flagging. Describes each heuristic, the thresholds in use, the review process, and how flagged submissions are handled in published analyses. Hand this to reviewers or ethics committees alongside any export.',
              size: '~140 KB',
              version: 'v1.0',
              lastRevised: 'May 2026',
            },
          ],
        },
        {
          title: 'Reference and metadata',
          intro: 'Documentation files needed to interpret the per-submission datasets. Download these alongside any analytical export.',
          items: [
            {
              name: 'Question dictionary',
              format: 'csv',
              description: 'One row per question. Columns: question code, food group, question text in DE/FR/IT/EN, answer scale, and scoring weights. The Rosetta Stone for the full quiz responses file.',
              rows: '67 rows',
              size: '~45 KB',
              versioned: true,
            },
            {
              name: 'Scoring methodology',
              format: 'pdf',
              description: 'How group scores and the overall score are calculated. Equations, weighting, edge cases, validation against the Australian HEQ baseline.',
              version: 'v1.0',
              size: '~180 KB',
              lastRevised: 'April 2026',
            },
            {
              name: 'Codebook',
              format: 'pdf',
              description: 'Full codebook describing each column in the per-submission datasets, value ranges, and missing-data conventions.',
              version: 'v1.0',
              size: '~220 KB',
              lastRevised: 'April 2026',
            },
          ],
        },
      ],
    };
  },
  methods: {
    downloadDataset(item) {
      // Placeholder — real implementation will hit the export API.
      // For now we just log so the dummy buttons aren't completely silent.
      console.log('[Data Export] would download:', item.name, item.format);
    },
    downloadBundle() {
      console.log('[Data Export] would assemble and download full bundle.');
    },
  },
};
</script>

<style scoped>
.data-export-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-good: #2D8659;
  --color-warn: #E8A33D;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.data-export-wrap *,
.data-export-wrap *::before,
.data-export-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 17px;
  color: var(--color-text-secondary);
  margin: 0 0 2rem;
  font-weight: 400;
  max-width: 720px;
}

.meta-strip {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  margin-bottom: 2.5rem;
  font-size: 13px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 500;
}

.meta-value {
  color: var(--color-text-primary);
  font-weight: 500;
}

.section {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-accent);
  display: inline-block;
}

.section-intro {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
  max-width: 760px;
}

.dataset-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dataset-card {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: center;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 16px 20px;
  transition: border-color 0.15s, background 0.15s;
}

.dataset-card:hover {
  border-color: var(--color-accent);
  background: var(--color-bg-soft);
}

.dataset-info {
  min-width: 0;
}

.dataset-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.dataset-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.dataset-format {
  font-size: 10px;
  font-weight: 600;
  color: var(--color-accent-dark);
  background: var(--color-accent-soft);
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dataset-format.csv { background: rgba(45, 134, 89, 0.12); color: #1A4A30; }
.dataset-format.zip { background: rgba(232, 163, 61, 0.15); color: #6B4A0F; }
.dataset-format.pdf { background: rgba(218, 41, 28, 0.12); color: #8a1d13; }

.dataset-tag {
  font-size: 10px;
  font-weight: 500;
  color: var(--color-text-tertiary);
  background: var(--color-bg-muted);
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dataset-tag.sensitive {
  background: rgba(232, 163, 61, 0.18);
  color: #6B4A0F;
}

.dataset-description {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 6px;
  line-height: 1.55;
}

.dataset-meta {
  display: flex;
  gap: 14px;
  font-size: 11px;
  color: var(--color-text-tertiary);
  flex-wrap: wrap;
}

.dataset-meta-item {
  display: flex;
  gap: 4px;
}

.dataset-meta-item strong {
  color: var(--color-text-secondary);
  font-weight: 500;
}

.download-button {
  background: var(--color-accent);
  color: white;
  border: none;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.download-button:hover {
  background: var(--color-accent-dark);
}

.download-button.secondary {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
  border: 0.5px solid var(--color-border);
}

.download-button.secondary:hover {
  background: var(--color-bg-card);
  border-color: var(--color-border-strong);
}

.privacy-note {
  background: var(--color-bg-soft);
  border-left: 3px solid var(--color-accent);
  border-radius: var(--radius-md);
  padding: 16px 20px;
  margin-bottom: 2rem;
}

.privacy-note-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 6px;
}

.privacy-note-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.bulk-card {
  background: linear-gradient(135deg, var(--color-bg-soft) 0%, rgba(83, 74, 183, 0.04) 100%);
  border: 0.5px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  padding: 24px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: center;
}

.bulk-card-title {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 6px;
}

.bulk-card-desc {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0;
}

.footer {
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 0.5px solid var(--color-border);
  font-size: 12px;
  color: var(--color-text-tertiary);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

@media (max-width: 700px) {
  .dataset-card,
  .bulk-card {
    grid-template-columns: 1fr;
  }
  .download-button { width: 100%; }
}
</style>
